// JSON message schemas for ASIS Voice v1. Hand-rolled validators keep the
// runtime zero-dep and the failure modes explicit. These are the *only* shapes
// the server and client accept over the WS text channel.
import { PROTOCOL_VERSION, SAMPLE_RATE } from './constants.js';

export type ClientHello = {
  type: 'hello';
  protocol: 1;
  token: string;
  auth?: 'browser';
  client: 'browser' | 'esp32' | 'test';
  mode: 'vad' | 'ptt';
  sampleRate: number;
};

export type ClientText = { type: 'text'; text: string };
export type ClientInterrupt = { type: 'interrupt'; epoch: number; playedSamples: number };
export type ClientPlaybackProgress = {
  type: 'playback.progress';
  epoch: number;
  playedSamples: number;
};
export type ClientPlaybackCleared = {
  type: 'playback.cleared';
  epoch: number;
  playedSamples: number;
};
export type ClientPlaybackDone = {
  type: 'playback.done';
  epoch: number;
  playedSamples: number;
};
export type ClientInputCommit = { type: 'input.commit' };
export type ClientInputClear = { type: 'input.clear' };
export type ClientMute = { type: 'mute'; muted: boolean };
export type ClientPing = { type: 'ping' };
export type ClientImage = { type: 'image'; mimeType: 'image/jpeg'; data: string };
export type ClientDeviceActionAck = {
  type: 'device.action.ack';
  id: string;
  executionToken: string;
  status: 'succeeded' | 'failed';
  code: 'ok' | 'unsupported' | 'busy' | 'local_cancelled' | 'not_authorized' | 'storage_failed' | 'hardware_failed' | 'invalid_state';
  controlEpoch: string;
};

export type ClientMessage =
  | ClientHello
  | ClientText
  | ClientInterrupt
  | ClientPlaybackProgress
  | ClientPlaybackCleared
  | ClientPlaybackDone
  | ClientInputCommit
  | ClientInputClear
  | ClientMute
  | ClientPing
  | ClientImage
  | ClientDeviceActionAck;

export type ServerReady = {
  type: 'ready';
  provider: string;
  model?: string;
  sampleRate: number;
  sessionId?: string;
  deviceActionAck?: true;
};
export type ServerState = {
  type: 'state';
  state:
    | 'connecting'
    | 'listening'
    | 'thinking'
    | 'speaking'
    | 'muted'
    | 'interrupted'
    | 'paused'
    | 'disconnected'
    | 'error'
    | 'idle';
};
export type ServerPlaybackStart = { type: 'playback.start'; epoch: number };
export type ServerPlaybackClear = { type: 'playback.clear'; epoch: number };
export type ServerPlaybackEnd = { type: 'playback.end'; epoch: number };
export type ServerPlaybackDelta = { type: 'playback.delta'; epoch: number; samples: Int16Array };
export type ServerTranscript = {
  type: 'transcript';
  role: 'user' | 'assistant';
  text: string;
  final: boolean;
  id?: string;
  interrupted?: boolean;
};
export type ServerEmotion = {
  type: 'emotion';
  emotion:
    | 'idle' | 'listen' | 'think' | 'speak' | 'success' | 'love' | 'cool'
    | 'happy' | 'laugh' | 'eyes_laugh' | 'wink' | 'confident' | 'oops'
    | 'shy' | 'awkward' | 'surprise' | 'shock' | 'angry' | 'sad' | 'sleep'
    | 'curious';
  ttlMs: number;
};
export type ServerMetrics = {
  type: 'metrics';
  firstAudioMs?: number;
  totalAudioMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  [k: string]: unknown;
};
export type ServerUsage = {
  type: 'usage';
  inputTokens?: number;
  outputTokens?: number;
  estimatedUsd?: number;
  [k: string]: unknown;
};
export type ServerError = { type: 'error'; code: string; message: string };
export type ServerPong = { type: 'pong' };
export type ServerSessionEnd = { type: 'session.end'; reason: string };
export type ServerDeviceAction = { type:'device.action'; action:{ id:string;generation:string;executionToken:string;type:string;expiresAt:number;ttlMs:number;controlEpoch:string;ownerScope:string;payload:Record<string,unknown> } };
export type ServerDeviceActionAcked = { type:'device.action.acked'; id:string };

export type ServerMessage =
  | ServerReady
  | ServerState
  | ServerPlaybackStart
  | ServerPlaybackClear
  | ServerPlaybackEnd
  | ServerPlaybackDelta
  | ServerTranscript
  | ServerEmotion
  | ServerMetrics
  | ServerUsage
  | ServerError
  | ServerPong
  | ServerSessionEnd
  | ServerDeviceAction
  | ServerDeviceActionAcked;

export class ProtocolError extends Error {
  constructor(message: string, public readonly code: string) {
    super(message);
    this.name = 'ProtocolError';
  }
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const DEVICE_ACTION_ACK_KEYS = new Set(['type', 'id', 'executionToken', 'status', 'code', 'controlEpoch']);
const DEVICE_ACTION_CODES = new Set([
  'ok', 'unsupported', 'busy', 'local_cancelled', 'not_authorized', 'storage_failed', 'hardware_failed', 'invalid_state',
]);

function canonicalActionToken(value: string): boolean {
  if (!/^[A-Za-z0-9_-]{43}$/.test(value)) return false;
  try {
    const padded = `${value.replace(/-/g, '+').replace(/_/g, '/')}=`;
    const decoded = atob(padded);
    if (decoded.length !== 32) return false;
    return btoa(decoded).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '') === value;
  } catch {
    return false;
  }
}

const IMAGE_MAX_DECODED_BYTES = 256 * 1024;

// Strict canonical base64 and JPEG structural check.
// Runs in Node and browser (no Buffer import).
function decodeStrictBase64(b64: string): Uint8Array {
  if (typeof b64 !== 'string') throw new ProtocolError('image data missing', 'bad_image');
  const len = b64.length;
  if (len === 0 || len % 4 !== 0 || len > 4 * Math.ceil(IMAGE_MAX_DECODED_BYTES / 3)) throw new ProtocolError('image data not canonical base64 or too large', 'bad_image');
  for (let i = 0; i < len; i++) {
    const c = b64.charCodeAt(i);
    const ok = (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || (c >= 48 && c <= 57) || c === 43 || c === 47 || c === 61;
    if (!ok) throw new ProtocolError('image data not canonical base64', 'bad_image');
  }
  let raw: string;
  try {
    raw = atob(b64);
    if (btoa(raw) !== b64) throw new Error('noncanonical padding bits');
  } catch {
    throw new ProtocolError('image data not canonical base64', 'bad_image');
  }
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  if (out.length === 0) throw new ProtocolError('image empty', 'bad_image');
  if (out.length > IMAGE_MAX_DECODED_BYTES) throw new ProtocolError('image too large', 'bad_image');
  return out;
}

function validateJpeg(bytes: Uint8Array): void {
  if (bytes.length < 4) throw new ProtocolError('image not jpeg', 'bad_image');
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8 || bytes[2] !== 0xff) throw new ProtocolError('image not jpeg', 'bad_image');
  // Scan segments. Skip APP0..APP15 markers 0xE0..0xEF and COM (0xFE) data
  // unless they look malformed. No full entropy-coded scan decode: we only
  // ensure structural integrity up to the SOI/EOI frame.
  let i = 2;
  let sawFrame = false;
  while (i < bytes.length) {
    if (bytes[i] !== 0xff) throw new ProtocolError('image jpeg malformed', 'bad_image');
    // Skip fill bytes.
    while (bytes[i + 1] === 0xff) i++;
    const marker = bytes[i + 1];
    if (marker === undefined || marker === 0x00 || marker === 0xd8) throw new ProtocolError('image jpeg malformed', 'bad_image');
    // Standalone markers (no payload): SOI(0xD8), EOI(0xD9), RST0-7(0xD0..0xD7),
    // TEM(0x01). Allow restart scanning; bail once EOI hit.
    if (marker === 0xd9) throw new ProtocolError('image jpeg has no scan', 'bad_image');
    if (marker >= 0xd0 && marker <= 0xd7) { i += 2; continue; }
    if (marker === 0x01) { i += 2; continue; }
    if (marker === 0xda) {
      // Start of scan: from here we only check that EOI eventually appears
      // before end of buffer. We do not parse entropy data.
      let j = i + 2;
      // Skip scan header.
      if (!sawFrame || j + 1 >= bytes.length) throw new ProtocolError('image jpeg malformed', 'bad_image');
      const segLen = (bytes[j]! << 8) | bytes[j + 1]!;
      if (segLen < 6 || j + segLen >= bytes.length) throw new ProtocolError('image jpeg malformed', 'bad_image');
      j += segLen;
      // Walk until we find an EOI marker that is not part of a fill sequence.
      let sawEoi = false;
      while (j < bytes.length) {
        if (bytes[j] === 0xff) {
          if (j + 1 < bytes.length && bytes[j + 1] === 0xd9) { sawEoi = j + 2 === bytes.length; break; }
          if (j + 1 < bytes.length && bytes[j + 1] !== 0x00 && (bytes[j + 1]! < 0xd0 || bytes[j + 1]! > 0xd7)) {
            // Another marker inside scan without restart — unusual but tolerated if followed by EOI.
          }
        }
        j++;
      }
      if (!sawEoi) throw new ProtocolError('image jpeg truncated', 'bad_image');
      return;
    }
    const hi = bytes[i + 2]!;
    const lo = bytes[i + 3]!;
    const segLen = (hi << 8) | lo;
    if (segLen < 2 || i + 2 + segLen > bytes.length) throw new ProtocolError('image jpeg malformed', 'bad_image');
    if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
      const height = (bytes[i + 5]! << 8) | bytes[i + 6]!;
      const width = (bytes[i + 7]! << 8) | bytes[i + 8]!;
      const components = bytes[i + 9] ?? 0;
      if (segLen < 8 || components < 1 || components > 4 || segLen !== 8 + components * 3 || height < 1 || width < 1) throw new ProtocolError('image jpeg frame malformed', 'bad_image');
      sawFrame = true;
    }
    i += 2 + segLen;
  }
  throw new ProtocolError('image jpeg truncated', 'bad_image');
}

export function parseClient(raw: unknown): ClientMessage {
  if (!isObj(raw)) throw new ProtocolError('message not an object', 'bad_shape');
  const type = raw['type'];
  if (typeof type !== 'string') throw new ProtocolError('type missing', 'bad_shape');

  switch (type) {
    case 'hello': {
      const protocol = raw['protocol'];
      const token = raw['token'];
      const browserAuth = raw['auth'] === 'browser';
      if (raw['auth'] !== undefined && !browserAuth) throw new ProtocolError('bad auth mode', 'bad_token');
      if (browserAuth && (token !== '' || raw['client'] !== 'browser')) throw new ProtocolError('browser auth requires empty token', 'bad_token');
      const client = raw['client'];
      const mode = raw['mode'];
      const sampleRate = raw['sampleRate'];
      if (protocol !== PROTOCOL_VERSION) {
        throw new ProtocolError(`unsupported protocol ${protocol}`, 'bad_protocol');
      }
      if (typeof token !== 'string' || (!browserAuth && token.length === 0) || token.length > 256) {
        throw new ProtocolError('token missing or too long', 'bad_token');
      }
      if (client !== 'browser' && client !== 'esp32' && client !== 'test') {
        throw new ProtocolError(`bad client ${String(client)}`, 'bad_client');
      }
      if (mode !== 'vad' && mode !== 'ptt') {
        throw new ProtocolError(`bad mode ${String(mode)}`, 'bad_mode');
      }
      if (sampleRate !== SAMPLE_RATE) {
        throw new ProtocolError(
          `sampleRate must be ${SAMPLE_RATE}, got ${String(sampleRate)}`,
          'bad_sample_rate',
        );
      }
      return { type: 'hello', protocol: 1, token, client, mode, sampleRate, ...(browserAuth ? { auth: 'browser' } : {}) };
    }
    case 'text': {
      const text = raw['text'];
      if (typeof text !== 'string' || text.length === 0 || text.length > 2000) {
        throw new ProtocolError('text missing or too long', 'bad_text');
      }
      return { type: 'text', text };
    }
    case 'interrupt': {
      const epoch = raw['epoch'];
      const played = raw['playedSamples'];
      if (!Number.isSafeInteger(epoch) || (epoch as number) < 0 || (epoch as number) > 0xffffffff) {
        throw new ProtocolError('bad epoch', 'bad_int');
      }
      if (!Number.isSafeInteger(played) || (played as number) < 0) {
        throw new ProtocolError('bad playedSamples', 'bad_int');
      }
      return { type: 'interrupt', epoch: epoch as number, playedSamples: played as number };
    }
    case 'playback.progress': {
      const epoch = raw['epoch'];
      const played = raw['playedSamples'];
      if (!Number.isSafeInteger(epoch) || (epoch as number) < 0 || (epoch as number) > 0xffffffff) {
        throw new ProtocolError('bad epoch', 'bad_int');
      }
      if (!Number.isSafeInteger(played) || (played as number) < 0) {
        throw new ProtocolError('bad playedSamples', 'bad_int');
      }
      return {
        type: 'playback.progress',
        epoch: epoch as number,
        playedSamples: played as number,
      };
    }
    case 'playback.cleared': {
      const epoch = raw['epoch'];
      const played = raw['playedSamples'];
      if (!Number.isSafeInteger(epoch) || (epoch as number) < 0 || (epoch as number) > 0xffffffff) {
        throw new ProtocolError('bad epoch', 'bad_int');
      }
      if (!Number.isSafeInteger(played) || (played as number) < 0) {
        throw new ProtocolError('bad playedSamples', 'bad_int');
      }
      return {
        type: 'playback.cleared',
        epoch: epoch as number,
        playedSamples: played as number,
      };
    }
    case 'playback.done': {
      const epoch = raw['epoch'];
      const played = raw['playedSamples'];
      if (!Number.isSafeInteger(epoch) || (epoch as number) < 0 || (epoch as number) > 0xffffffff) {
        throw new ProtocolError('bad epoch', 'bad_int');
      }
      if (!Number.isSafeInteger(played) || (played as number) < 0) {
        throw new ProtocolError('bad playedSamples', 'bad_int');
      }
      return {
        type: 'playback.done',
        epoch: epoch as number,
        playedSamples: played as number,
      };
    }
    case 'input.commit':
      return { type: 'input.commit' };
    case 'input.clear':
      return { type: 'input.clear' };
    case 'mute': {
      const muted = raw['muted'];
      if (typeof muted !== 'boolean') {
        throw new ProtocolError('muted must be boolean', 'bad_mute');
      }
      return { type: 'mute', muted };
    }
    case 'ping':
      return { type: 'ping' };
    case 'device.action.ack': {
      if (Object.keys(raw).some(key => !DEVICE_ACTION_ACK_KEYS.has(key)) || Object.keys(raw).length !== DEVICE_ACTION_ACK_KEYS.size) {
        throw new ProtocolError('device action ack has extra or missing fields', 'bad_device_action_ack');
      }
      const id = raw['id'];
      const executionToken = raw['executionToken'];
      const status = raw['status'];
      const code = raw['code'];
      const controlEpoch = raw['controlEpoch'];
      if (typeof id !== 'string' || id.length === 0 || id.length > 128) throw new ProtocolError('bad action id', 'bad_device_action_ack');
      if (typeof executionToken !== 'string' || !canonicalActionToken(executionToken)) throw new ProtocolError('bad execution token', 'bad_device_action_ack');
      if (status !== 'succeeded' && status !== 'failed') throw new ProtocolError('bad action status', 'bad_device_action_ack');
      if (typeof code !== 'string' || !DEVICE_ACTION_CODES.has(code)) throw new ProtocolError('bad action code', 'bad_device_action_ack');
      if ((status === 'succeeded') !== (code === 'ok')) throw new ProtocolError('inconsistent action result', 'bad_device_action_ack');
      if (typeof controlEpoch !== 'string' || !/^[a-f0-9]{32}$/.test(controlEpoch)) throw new ProtocolError('bad control epoch', 'bad_device_action_ack');
      return { type, id, executionToken, status, code: code as ClientDeviceActionAck['code'], controlEpoch };
    }
    case 'image': {
      const mimeType = raw['mimeType'];
      if (mimeType !== 'image/jpeg') throw new ProtocolError('unsupported image mime', 'bad_image');
      const data = raw['data'];
      if (typeof data !== 'string') throw new ProtocolError('image data missing', 'bad_image');
      const bytes = decodeStrictBase64(data);
      validateJpeg(bytes);
      return { type: 'image', mimeType: 'image/jpeg', data };
    }
    default:
      throw new ProtocolError(`unknown type ${type}`, 'unknown_type');
  }
}

// Looser parser used by tests/dev to inspect arbitrary payloads.
export function safeParseClient(raw: string): ClientMessage {
  const parsed = JSON.parse(raw);
  return parseClient(parsed);
}
